import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../services/api';
import { useAtozasTranslate } from '../../context/AtozasLanguageContext';
import LanguageSelector from '../common/LanguageSelector';
import ConfirmationModal from '../common/ConfirmationModal';

const Navbar = ({ onBecomeDriver, onDriverDashboard, onLogout, onHome, onProfile, onRides, user, isDriver = false, isOnDriverDashboard = false, onDriverTabChange, activeDriverTab = 'dashboard', onNotificationClick, unreadCount = 0, onMenuClick }) => {
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const hasNotifications = unreadCount > 0;
  const tLanguage = useAtozasTranslate('Language');
  const tHome = useAtozasTranslate('Home');
  const tDashboard = useAtozasTranslate('Dashboard');
  const tMyRides = useAtozasTranslate('My Rides');
  const tDriverEarnings = useAtozasTranslate('Driver Earnings');
  const tAccount = useAtozasTranslate('Account');
  const tRides = useAtozasTranslate('Rides');
  const tProfile = useAtozasTranslate('Profile');
  const tFeedback = useAtozasTranslate('Feedback');
  const tTerms = useAtozasTranslate('Terms & Conditions');
  const tPrivacy = useAtozasTranslate('Privacy Policy');
  const tLogout = useAtozasTranslate('Logout');
  const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
  const tBecomeDriver = useAtozasTranslate('Become a Driver');

  // Helper function to get profile picture URL
  const getProfileImageUrl = (profilePicture) => {
    if (!profilePicture) return null;
    // If it's a full URL, return as is
    if (profilePicture.startsWith('http')) return profilePicture;
    // Otherwise, construct backend URL using centralized API helper
    const baseUrl = getApiUrl();
    return `${baseUrl}/${profilePicture.replace(/\\/g, '/')}`;
  };

  const handleNotificationClick = () => {
    if (onNotificationClick) {
      onNotificationClick();
    }
    // Don't toggle panel here - let parent component handle navigation
  };
  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Menu Icon - Mobile Only */}
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="md:hidden p-2 text-gray-600 hover:text-primary-600 transition-colors mr-2"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}

          {/* Logo and App Name */}
          <div className="flex items-center space-x-3">
            <button
              onClick={onHome}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              {/* Logo - Hidden on mobile */}
              <div className="hidden md:flex w-10 h-10 md:w-12 md:h-12 rounded-full bg-black items-center justify-center p-1">
                <img
                  src="/icon-192.png"
                  alt="UttaApp Logo"
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              {/* App Name - Visible on all screens */}
              <h1 className="font-bold text-gray-900 flex flex-col items-start leading-tight">
                <span className="text-xl md:text-2xl">UttaApp</span>
                <span className="text-xs md:text-sm font-normal opacity-70 whitespace-nowrap">beta/test version</span>
              </h1>
            </button>
          </div>

          {/* Navigation Items - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-8">
            {isOnDriverDashboard ? (
              <>
                {/* Driver Dashboard Tabs */}
                <button
                  onClick={onHome}
                  className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                >
                  {tHome}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('dashboard')}
                  className={`transition-colors font-medium ${activeDriverTab === 'dashboard'
                      ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                      : 'text-gray-600 hover:text-primary-600'
                    }`}
                >
                  {tDashboard}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('rides')}
                  className={`transition-colors font-medium ${activeDriverTab === 'rides'
                      ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                      : 'text-gray-600 hover:text-primary-600'
                    }`}
                >
                  {tMyRides}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('earnings')}
                  className={`transition-colors font-medium ${activeDriverTab === 'earnings'
                      ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                      : 'text-gray-600 hover:text-primary-600'
                    }`}
                >
                  {tDriverEarnings}
                </button>
                <button
                  onClick={() => onDriverTabChange && onDriverTabChange('account')}
                  className={`transition-colors font-medium ${activeDriverTab === 'account'
                      ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                      : 'text-gray-600 hover:text-primary-600'
                    }`}
                >
                  {tAccount}
                </button>
              </>
            ) : (
              <>
                {/* User Dashboard Tabs */}
                <button
                  onClick={onHome}
                  className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                >
                  {tHome}
                </button>
                <button
                  onClick={onRides || (() => { })}
                  className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                >
                  {tRides}
                </button>
                <button
                  onClick={onProfile}
                  className="text-gray-600 hover:text-primary-600 transition-colors font-medium"
                >
                  {tProfile}
                </button>
              </>
            )}
          </div>

          {/* Driver Action Button - Hidden on mobile */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="flex items-center gap-2">
              <LanguageSelector
                aria-label={tLanguage}
                title={tLanguage}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[170px]"
              />
              <button
                type="button"
                onClick={() => navigate('/subscription-pricing')}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/90 backdrop-blur px-3 py-1 text-sm text-gray-800 shadow-sm hover:shadow-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition"
              >
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
                </svg>
                <span className="font-semibold">{tAccount}</span>
              </button>
            </div>

            {isDriver && !isOnDriverDashboard ? (
              <button
                onClick={onDriverDashboard}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
              >
                {tDriverDashboard}
              </button>
            ) : !isDriver ? (
              <button
                onClick={onBecomeDriver}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
              >
                {tBecomeDriver}
              </button>
            ) : null}




            {/* Notification Bell Icon - Only shown on driver dashboard */}
            {isOnDriverDashboard && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
                  aria-label="Notifications"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {/* Notification Badge */}
                  {hasNotifications && (
                    <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 border-2 border-white transform translate-x-1 -translate-y-1">
                      <span className="text-white text-xs font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    </span>
                  )}
                </button>
              </div>
            )}

      
            {/* User Profile Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center overflow-hidden relative">
                  {getProfileImageUrl(user?.profilePicture) ? (
                    <img
                      src={getProfileImageUrl(user.profilePicture)}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full absolute inset-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement?.querySelector('.profile-fallback');
                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <span
                    className="profile-fallback text-white font-semibold text-sm"
                    style={{ display: getProfileImageUrl(user?.profilePicture) ? 'none' : 'flex' }}
                  >
                    {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                <span className="hidden sm:block text-sm font-medium">{user?.username || 'User'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */} 
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{user?.username || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate" title={user?.email || ''}>{user?.email || ''}</p>
                  </div>
                  <button
                    onClick={onProfile}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {tProfile}
                  </button>
                  <div className="border-t border-gray-100 mt-1">
                    <a
                      href="/feedback"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        // Navigate using window.location or navigate hook if available
                        window.location.href = '/feedback';
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {tFeedback}
                    </a>
                    <a
                      href="/terms-conditions"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        window.location.href = '/terms-conditions';
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {tTerms}
                    </a>
                    <a
                      href="/privacy-policy"
                      onClick={(e) => {
                        e.preventDefault();
                        if (onProfile) onProfile();
                        window.location.href = '/privacy-policy';
                      }}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {tPrivacy}
                    </a>
                  </div>
                  <div className="border-t border-gray-100 mt-1">
                    <button
                      onClick={() => setShowLogoutConfirm(true)}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      {tLogout}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile notification bell - Only shown on driver dashboard (mobile) - Right side */}
          <div className="md:hidden flex items-center gap-2 ml-auto">
            {/* Mobile Language Selector */}
            <LanguageSelector
              aria-label={tLanguage}
              title={tLanguage}
              className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 max-w-[120px]"
            />
            <button
              type="button"
              onClick={() => navigate('/subscription-pricing')}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white/90 backdrop-blur px-2 py-1 text-xs text-gray-800 shadow-sm hover:shadow-md hover:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition"
            >
              <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21a9 9 0 100-18 9 9 0 000 18z" />
              </svg>
              <span className="font-semibold">{tAccount}</span>
            </button>
            {isOnDriverDashboard && (
              <>
                {/* Notification Bell */}
                <button
                  onClick={handleNotificationClick}
                  className="relative p-2 text-gray-600 hover:text-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
                  aria-label="Notifications"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {/* Notification Badge */}
                  {hasNotifications && (
                    <span className="absolute top-0 right-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 border-2 border-white transform translate-x-1 -translate-y-1">
                      <span className="text-white text-xs font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
                    </span>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Show appropriate driver button - Hidden on driver dashboard */}
      {!isOnDriverDashboard && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-3">
            {isDriver && !isOnDriverDashboard ? (
              <button
                onClick={onDriverDashboard}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                {tDriverDashboard}
              </button>
            ) : !isDriver ? (
              <button
                onClick={onBecomeDriver}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                {tBecomeDriver}
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        show={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          onLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </nav>
  );
};

export default Navbar;

